import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../../api";
import { Badge, BadgeList } from "../../../components/badge";
import { Button, ButtonRow, PrimaryButton } from "../../../components/button";
import { Card, CardNavigation } from "../../../components/card";
import { CodeEditor } from "../../../components/code-editor";
import { CommandBar } from "../../../components/command-bar";
import { Dashboard } from "../../../components/dashboard";
import { Definition, DefinitionList } from "../../../components/definition";
import { Editor, EditorDescription, EditorTitle } from "../../../components/editor";
import { TagsInput, TextInput } from "../../../components/form";
import { Icon } from "../../../components/icon";
import { Padding } from "../../../components/layout";
import { Playground, PlaygroundAside, PlaygroundAsideContainer, PlaygroundContent } from "../../../components/playground";
import { HorizontalSizableContainer, HorizontalSizableSection, SizableContainer, SizableSection } from "../../../components/sizable-container";
import { TabControl } from "../../../components/tabcontrol";
import { CheckWrapper, MutedText } from "../../../components/text";
import { displayAlert, displayFormControl } from "../../../ui/alert";
import { formatNumber } from "../../../ui/format";
import { runOperation } from "../../../ui/operation";
import { find, pipe, valueFrom } from "../../../util";
import { Dropdown, DropdownDivider, DropdownItem } from "../../../components/dropdown";
import { JsonControl } from "../../../components/json-control";

export default function (ctx) {

    const collectionId = ctx.params.id;
    const documentId = ctx.params.documentid;

    const sectionRegex = /^-{3,}/gm;
    const document = el.state.create({});
    const documentText = el.state.create("");
    const isCreatingDocument = documentId == "create";

    if (!isCreatingDocument) {
        runOperation("Fetching document...", async () => {
            const response = await api.getJson(`/api/v1/collections/${collectionId}/documents/${documentId}`);
            document.value = response.data;
            documentText.value = response.data.contents;
        });
    } else {
        documentText.value = "";
    }

    function handleSave() {

        const splitSections = valueFrom("documentSplitSections") || false;
        var pushList = [];

        const baseName = valueFrom("documentName") || document.value.name;
        const tags = valueFrom("documentTags");
        const reference = valueFrom("documentReference");
        const rawText = valueFrom("documentContents").trim();

        if (splitSections) {

            pushList = rawText
                .split(sectionRegex)
                .filter(x => x)
                .map((x, i) => ({ name: baseName + ":" + i, tags, reference, contents: x.trim() }));

        } else {
            pushList = [{
                name: baseName,
                tags,
                reference,
                contents: rawText
            }];
        }

        function doSave() {
            runOperation("Saving...", async () => {
                const promises = pushList.map(x => api.putJson(`/api/v1/collections/${collectionId}/documents`, x));
                await Promise.all(promises);
            });
        }

        if (!isCreatingDocument && sectionRegex.test(rawText)) {
            displayAlert({
                title: "Confirm Save",
                message: "This document contains section separators (---). As you are editing an existing document, saving will not split this document into multiple other documents. Do you want to continue?",
                type: "alert-warning",
                buttons: [
                    {
                        label: "Cancel"
                    },
                    {
                        label: "Yes, save anyway",
                        onClick() {
                            doSave();
                        }
                    }
                ]
            });
        } else {
            doSave();
        }
    }

    return el.state.listening([document], () =>
        Dashboard(
            {
                classList: ["full-size"]
            },
            Card(
                CardNavigation(
                    CommandBar(
                        Button(
                            Icon("arrow-left-line"),
                            "Back",
                            {
                                onClick() {
                                    history.go(-1);
                                    page();
                                }
                            }
                        ),
                        PrimaryButton(
                            Icon("save-line"),
                            isCreatingDocument ? "Create document" : "Save changes",
                            {
                                onClick: handleSave
                            }
                        )
                    )
                ),
                Playground(
                    HorizontalSizableContainer(
                        HorizontalSizableSection(
                            {
                                initialSize: "400px"
                            },
                            PlaygroundAside(
                                TabControl([
                                    {
                                        title: "Parameters",
                                        icon: Icon("equalizer-2-fill"),
                                        contents: PlaygroundAsideContainer(
                                            SizableContainer(
                                                SizableSection("Document parameters",
                                                    isCreatingDocument && Editor(
                                                        {
                                                            id: "documentName",
                                                            value: document.value.name,
                                                            renderValue(value) {
                                                                return Button(value || "Type the document name");
                                                            },
                                                            manipulateValue(value, callback) {
                                                                displayFormControl({
                                                                    title: this.editorTitle,
                                                                    message: this.editorParagraphs,
                                                                    formControlItem: TextInput(null, "Document name", { value }),
                                                                    onConfirm: callback,
                                                                    validate: [
                                                                        [x => x.length > 3, "Name must be at least 3 characters long"],
                                                                    ]
                                                                });
                                                            }
                                                        },
                                                        EditorDescription(
                                                            EditorTitle("Document name")
                                                        )
                                                    ),
                                                    Editor(
                                                        {
                                                            id: "documentTags",
                                                            value: document.value.tags,
                                                            renderValue(value) {
                                                                return Button(
                                                                    BadgeList(
                                                                        value?.length > 0 ? value?.slice(0, 4).map(tag => Badge(tag)) : MutedText("No tags")
                                                                    )
                                                                )
                                                            },
                                                            manipulateValue(value, callback) {
                                                                displayFormControl({
                                                                    title: this.editorTitle,
                                                                    message: this.editorParagraphs,
                                                                    formControlItem: TagsInput("tags", { value }),
                                                                    onConfirm: callback
                                                                });
                                                            }
                                                        },
                                                        EditorDescription(
                                                            EditorTitle("Tags")
                                                        )
                                                    ),
                                                    Editor(
                                                        {
                                                            id: "documentReference",
                                                            value: document.value.reference,
                                                            renderValue(value) {
                                                                return Button(
                                                                    value || MutedText("No reference ID")
                                                                )
                                                            },
                                                            manipulateValue(value, callback) {
                                                                displayFormControl({
                                                                    title: this.editorTitle,
                                                                    message: this.editorParagraphs,
                                                                    formControlItem: TextInput("referenceId", "Reference ID", { value }),
                                                                    onConfirm: callback
                                                                });
                                                            }
                                                        },
                                                        EditorDescription(
                                                            EditorTitle("Reference ID")
                                                        )
                                                    ),
                                                    isCreatingDocument && Editor(
                                                        {
                                                            id: "documentSplitSections",
                                                            value: false,
                                                            renderValue(value) {
                                                                return CheckWrapper(value, "yes", "no")
                                                            },
                                                            manipulateValue(value, callback) {
                                                                callback(!!!value);
                                                            }
                                                        },
                                                        EditorDescription(
                                                            EditorTitle("Split multiple sections")
                                                        )
                                                    )
                                                ),
                                                SizableSection("Document information",
                                                    el.state.listening([documentText], () =>
                                                        Padding(0.5,
                                                            documentText.modified && DefinitionList(
                                                                pipe({ tokenCount: (documentText.value.length / 4) }, v =>
                                                                    Definition(
                                                                        "Aproximate token count",
                                                                        formatNumber(v.tokenCount)
                                                                    )
                                                                ),
                                                                pipe({ count: documentText.value.split(' ').filter(x => x).length }, c =>
                                                                    Definition(
                                                                        "Word count",
                                                                        formatNumber(c.count), " ",
                                                                        c.count > 700 && Icon("alert-line", { style: { color: "#cc6a0fff" }, title: "Too many words." }),
                                                                        c.count < 20 && Icon("alert-line", { style: { color: "#cc6a0fff" }, title: "Too few words." })
                                                                    )
                                                                ),
                                                                Definition(
                                                                    "Character count",
                                                                    formatNumber(documentText.value.length)
                                                                ),
                                                                isCreatingDocument && Definition(
                                                                    "Section count",
                                                                    formatNumber(documentText.value.split(sectionRegex).filter(x => x).length)
                                                                )
                                                            )
                                                        )
                                                    )
                                                )
                                            )
                                        )
                                    },
                                    {
                                        title: "Metadata",
                                        icon: Icon("file-code-line"),
                                        contents: PlaygroundAsideContainer(
                                            el.state.listening([document], () =>
                                                SizableContainer(
                                                    SizableSection("Metadata",
                                                        Padding(0.5,
                                                            JsonControl({
                                                                value: document?.value.metadata ?? {}
                                                            })
                                                        )
                                                    )
                                                )
                                            )
                                        )
                                    },
                                ], {
                                    type: "vertical"
                                })
                            ),
                        ),
                        HorizontalSizableSection(
                            {
                                initialSize: "calc(100% - 400px)"
                            },
                            PlaygroundContent(
                                {
                                    style: {
                                        "overflow": "hidden"
                                    }
                                },
                                CodeEditor({
                                    value: document.value.contents,
                                    props: {
                                        id: "documentContents",
                                        height: "auto",
                                        onInput(event) {
                                            documentText.value = event.target.value;
                                        },
                                        style: {
                                            height: "auto",
                                            minHeight: "100%",
                                        }
                                    },
                                    wrapperProps: {
                                        style: {
                                            height: "100%",
                                            width: "100%",
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            border: "none",
                                            overflowY: "scroll"
                                        }
                                    }
                                })
                            )
                        )
                    )
                )
            )
        )
    );
}
